<template>
  <div class="alerts-body" :class="`alerts-body--${mode}`">
    <p v-if="alertsStore.isLoading" class="alerts-body__status" data-qa="alerts-body-loading">
      Loading alerts...
    </p>

    <p v-else-if="alertsStore.isError" class="alerts-body__status" data-qa="alerts-body-error">
      We couldn't load your alerts right now.
    </p>

    <p v-else-if="!sections.length" class="alerts-body__empty" data-qa="alerts-body-empty">
      You have no alerts
    </p>

    <section v-for="section in sections" v-else :key="section.heading" class="alerts-body__section">
      <h3 class="alerts-body__section-heading">{{ section.heading }}</h3>
      <ul class="alerts-body__list">
        <li v-for="alert in section.alerts" :key="alert.alertId">
          <button class="alerts-body__item" type="button" @click="onAlertClick(alert)">
            <div class="alerts-body__item-icon" :class="`alerts-body__item-icon--${getAlertImage(alert.alertType)}`"
              aria-hidden="true">
              <img :src="getAlertIcon(alert.alertType)" width="20" height="20" alt="" />
            </div>
            <span class="alerts-body__item-content">
              <span class="alerts-body__item-title"
                :class="{ 'alerts-body__item-title--unread': !alert.viewed }">
                {{ alert.title }}
              </span>
              <span class="alerts-body__item-date">{{ formatDate(alert.reportedOn) }}</span>
            </span>
            <img :src="chevronIcon" class="alerts-body__item-chevron" width="16" height="16" alt=""
              aria-hidden="true" />
          </button>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import documentIcon from '@/assets/icons/document.svg';
import lineChartDown from '@/assets/icons/line-chart-down.svg';
import lineChartUp from '@/assets/icons/line-chart-up.svg';
import chevronIcon from '@/assets/icons/right-chevron-alert.svg';
import { useAlertsStore } from '@/stores';
import { AlertWrapper } from '@/types/responseTypes';
import { getAlertImage } from '@/utils/alertImage';
import { getDaysAgo } from '@/utils/getDaysAgo';

interface Props {
  mode?: 'widget' | 'full';
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'full',
});

const ALERT_ICON_MAP = {
  image_good: lineChartUp,
  image_bad: lineChartDown,
  document: documentIcon,
} as const;

const getAlertIcon = (alertType: AlertWrapper['alertType']) =>
  ALERT_ICON_MAP[getAlertImage(alertType)];

const emit = defineEmits<{
  (e: 'alert-selected', alertId: number): void;
}>();

const alertsStore = useAlertsStore();

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

const sortedAlerts = computed<AlertWrapper[]>(() =>
  [...(alertsStore.alerts ?? [])]
    .sort((a, b) => new Date(b.reportedOn).getTime() - new Date(a.reportedOn).getTime()),
);

const widgetAlerts = computed<AlertWrapper[]>(() =>
  [...(alertsStore.alerts ?? [])]
    .sort((a, b) => new Date(b.reportedOn).getTime() - new Date(a.reportedOn).getTime())
    .slice(0, 5),
);

const recentAlerts = computed<AlertWrapper[]>(() =>
  sortedAlerts.value.filter((a) => getDaysAgo(a.reportedOn) <= 7),
);

const thisMonthAlerts = computed<AlertWrapper[]>(() =>
  sortedAlerts.value.filter((a) => {
    const d = getDaysAgo(a.reportedOn);
    return d > 7 && d <= 30;
  }),
);

const olderAlerts = computed<AlertWrapper[]>(() =>
  sortedAlerts.value.filter((a) => getDaysAgo(a.reportedOn) > 30),
);


interface AlertSection {
  heading: string;
  alerts: AlertWrapper[];
}

const sections = computed<AlertSection[]>(() => {
  const allSections =
    props.mode === 'widget'
      ? [
        { heading: 'Last 7 days', alerts: widgetAlerts.value.filter((a) => getDaysAgo(a.reportedOn) <= 7) },
        {
          heading: 'Last 30 days',
          alerts: widgetAlerts.value.filter((a) => {
            const d = getDaysAgo(a.reportedOn);
            return d > 7 && d <= 30;
          }),
        },
        {
          heading: 'Previous',
          alerts: widgetAlerts.value.filter((a) => getDaysAgo(a.reportedOn) > 30),
        },
      ]
      : [
        { heading: 'Last 7 days', alerts: recentAlerts.value },
        { heading: 'Last 30 days', alerts: thisMonthAlerts.value },
        { heading: 'Previous', alerts: olderAlerts.value },
      ];
  return allSections.filter((s) => s.alerts.length > 0);
});

const onAlertClick = (alert: AlertWrapper) => {
  alertsStore.openAlertDetail(alert.alertId);
  if (!alert.viewed) {
    void alertsStore.markAlertAsViewed(alert.alertId);
  }
  emit('alert-selected', alert.alertId);
};
</script>

<style scoped lang="scss">
.alerts-body {
  padding: 0 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  &--widget {
    .alerts-body__section-heading {
      font-size: remc(16px);
    }
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  &__section-heading {
    color: $brand-primary-100-navy;
    font-family: "Museo Slab";
    font-size: remc(18px);
    line-height: remc(24px);
    font-weight: 700;
    margin: 0 0 4px;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px 0;
    cursor: pointer;
    background: transparent;
    border: 0;
    text-align: left;
  }

  &__item-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background: $blue-42;
    flex-shrink: 0;
    color: $brand-primary-100-navy;
  }

  &__item-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__item-title {
    color: $blue-34;
    font-family: $helvetica;
    font-size: remc(14px);
    font-style: normal;
    font-weight: 400;
    line-height: remc(20px);
    margin: 0;
  }

  &__item-title--unread {
    font-weight: 700;
  }

  &__item-date {
    color: $blue-33;
    font-size: remc(14px);
    line-height: remc(20px);
    font-style: normal;
    font-family: $helvetica;
    margin: 0;
  }

  &__item-chevron {
    flex-shrink: 0;
  }

  &__empty {
    padding: 24px 16px;
    color: $brand-secondary-100-light-navy;
    font-size: remc(14px);
    line-height: remc(20px);
    text-align: center;
  }

  &__status {
    padding: 24px 16px;
    color: $brand-secondary-100-light-navy;
    font-size: remc(14px);
    line-height: remc(20px);
    text-align: center;
  }
}
</style>
